/**
 * OF-001F #2 — Workspace Switcher (single-function atomic switch).
 *
 * OWNER VALIDATION FAILURE 2: clicking the switcher could send the URL
 * to `/clinic/home` first, then bounce back to the previous workspace.
 * Root cause was the SPLIT transaction — RBAC state was committed in
 * `setActiveWorkspace`, but the URL change was a SEPARATE
 * `useNavigate()` call in this component.  Between the two, the React
 * Router navigator could serialise its own state machine and let a
 * guard reading stale storage land the user on `/clinic/home`.
 *
 * Fix: the click handler calls EXACTLY ONE function —
 * `switchWorkspace(workspaceId)`.  That function persists, commits,
 * and changes the URL atomically inside RBACContext.  No
 * `useNavigate()` here.  No `/clinic/home` intermediate.
 *
 * Drift guards:
 *   - `test_switcher_calls_only_switchworkspace`
 *   - `test_switcher_does_not_use_navigate_or_window_location_hash`
 *   - `test_p65_after_sales_to_sales_no_clinic_home_intermediate`
 */
import React, { useState, useRef, useEffect } from 'react';
import { useRBAC } from '../context/RBACContext';
import { Badge } from './ui/badge';
import { ChevronDown, Briefcase, Heart, Check, AlertCircle } from 'lucide-react';

const TYPE_ICON = {
  patient: Heart,
  provider: Briefcase,
};

const TYPE_BADGE_CLASS = {
  patient: 'bg-sky-100 text-sky-800',
  provider: 'bg-emerald-100 text-emerald-800',
};

export default function WorkspaceSwitcher() {
  const { workspaces, activeWorkspaceId, canSwitchWorkspace, switchWorkspace } = useRBAC();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // OF-001B §6.F — show ONLY for multi-workspace users.
  if (!canSwitchWorkspace) return null;

  const active = (workspaces || []).find(w => w.workspace_id === activeWorkspaceId);
  const ActiveIcon = TYPE_ICON[active?.type] || Briefcase;

  // OF-001F #2 — single-function atomic switch.  The handler does
  // EXACTLY ONE thing: call `switchWorkspace(id)`.  That function
  // persists + commits + navigates atomically inside RBACContext.
  // The owner-mandated contract is "one function only".
  // MTMR-P0 — the switch is asynchronous because the SERVER must
  // confirm the new provider + role context first. On refusal the menu
  // stays open and the current workspace/data are untouched.
  const handleSwitch = async (ws) => {
    if (!ws.is_available || !ws.default_route) return;
    const result = await switchWorkspace(ws.workspace_id);
    if (result) setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative" data-testid="workspace-switcher-root">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-800 hover:bg-slate-200 transition-colors text-sm font-medium"
        data-testid="workspace-switcher-button"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <ActiveIcon className="w-3.5 h-3.5" />
        <span className="truncate max-w-[160px]" data-testid="workspace-switcher-active-label">
          {active?.display_name || 'Choose workspace'}
        </span>
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-slate-200 py-1 z-50"
          data-testid="workspace-switcher-menu"
          role="listbox"
        >
          <div className="px-3 py-2 text-[11px] text-slate-500 font-semibold uppercase tracking-wide border-b">
            Switch workspace
          </div>
          {(workspaces || []).map(ws => {
            const Icon = TYPE_ICON[ws.type] || Briefcase;
            const isActive = ws.workspace_id === activeWorkspaceId;
            const disabled = !ws.is_available || !ws.default_route;
            return (
              <button
                key={ws.workspace_id}
                type="button"
                onClick={() => handleSwitch(ws)}
                disabled={disabled}
                className={`w-full text-left px-3 py-2.5 text-sm flex items-start gap-2 transition-colors ${
                  isActive
                    ? 'bg-slate-50 text-slate-900 font-medium'
                    : disabled
                      ? 'text-slate-400 cursor-not-allowed'
                      : 'hover:bg-slate-50 text-slate-700'
                }`}
                data-testid={`workspace-option-${ws.workspace_id}`}
                role="option"
                aria-selected={isActive}
              >
                <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate">{ws.display_name}</span>
                    <Badge className={`${TYPE_BADGE_CLASS[ws.type] || 'bg-slate-100 text-slate-700'} text-[9px] uppercase tracking-wide px-1.5 py-0`}>
                      {ws.type}
                    </Badge>
                  </div>
                  {ws.clinic_name && ws.type === 'provider' && (
                    <div className="text-[11px] text-slate-500 truncate">{ws.clinic_name}</div>
                  )}
                  {!ws.is_available && ws.reason_if_unavailable && (
                    <div className="flex items-start gap-1 mt-0.5 text-[10.5px] text-amber-700">
                      <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span>{ws.reason_if_unavailable}</span>
                    </div>
                  )}
                </div>
                {isActive && <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
